import React, { useState } from 'react';
import {
	Box,
	Button,
	HStack,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Stack,
	Text,
	Tooltip,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { FaChevronDown, FaGoogle, FaPaperPlane } from 'react-icons/fa';
import {
	useConnectGmail,
	useDisconnectGmail,
	useGmailStatus,
	useSyncGmailInbox,
} from '../../../hooks/useGmailConnection';
import { useSendLeadEmail } from '../../../hooks/CRM/useOutreach';
import { useOutreachEmailSettings } from '../../../hooks/useOutreachEmailSettings';
import OutreachEmailHtmlPreview from './OutreachEmailHtmlPreview';
import { EVENT_LABELS, NEXT_SEND, primaryOf } from './leadMeta';

const SEND_LABELS = {
	emailed: 'Send first email',
	follow_up_1: 'Send follow-up 1',
	follow_up_2: 'Send follow-up 2',
};

const BODY_FIELD = {
	emailed: 'body',
	follow_up_1: 'followUp1',
	follow_up_2: 'followUp2',
};

const replySubject = (subject) =>
	/^re:/i.test(subject || '') ? subject : `Re: ${subject || ''}`;

/** What the next send will look like; mirrors the server's choice of recipient and subject. */
const previewFor = (lead, kind, draft, formatEmail = (b) => ({ text: b, html: b })) => {
	const d = draft || lead.draft || {};
	const thread = lead.emailThread || {};
	const isFollowUp = kind !== 'emailed';
	const formatted = formatEmail(d[BODY_FIELD[kind]] || '');
	return {
		to: (isFollowUp && thread.to) || d.to || primaryOf(lead)?.email || '',
		subject: isFollowUp
			? replySubject(thread.subject || d.subject)
			: d.subject || '',
		body: formatted.text,
		bodyHtml: formatted.html,
		threadMissing: isFollowUp && !thread.threadId,
	};
};

/** Header control: connect, reconnect or disconnect the signed-in user's Gmail. */
export const GmailConnectButton = ({ size = 'sm' }) => {
	const toast = useToast();
	const { data: status, isLoading } = useGmailStatus();
	const connect = useConnectGmail();
	const disconnect = useDisconnectGmail();
	const syncInbox = useSyncGmailInbox();

	const onConnect = () =>
		connect.mutate(undefined, {
			onError: (err) =>
				toast({ status: 'error', title: err.message, isClosable: true }),
		});

	if (isLoading) return null;

	if (status?.configured === false) {
		return (
			<Tooltip label='Gmail sending is not set up on the server yet' hasArrow>
				<Box>
					<Button size={size} variant='outline' leftIcon={<FaGoogle />} isDisabled>
						Connect Gmail
					</Button>
				</Box>
			</Tooltip>
		);
	}

	// Missing readonly (bounce/reply tracking) or broken token → reconnect.
	if (!status?.connected || status?.needsReconnect) {
		const label = status?.needsReconnect
			? status?.canTrackInbox === false
				? 'Reconnect Gmail to track replies'
				: 'Reconnect Gmail'
			: 'Connect Gmail';
		return (
			<Tooltip
				label={
					status?.error ||
					(status?.canTrackInbox === false
						? 'Allow inbox access so we can detect bounces and replies'
						: undefined)
				}
				isDisabled={!status?.needsReconnect}
				hasArrow>
				<Button
					size={size}
					variant='outline'
					colorScheme={status?.needsReconnect ? 'orange' : 'gray'}
					leftIcon={<FaGoogle />}
					isLoading={connect.isPending}
					onClick={onConnect}>
					{label}
				</Button>
			</Tooltip>
		);
	}

	return (
		<Menu placement='bottom-end'>
			<MenuButton
				as={Button}
				size={size}
				variant='outline'
				colorScheme='green'
				leftIcon={<FaGoogle />}
				rightIcon={<FaChevronDown />}
				isLoading={disconnect.isPending || syncInbox.isPending}>
				{status.email || 'Gmail connected'}
			</MenuButton>
			<MenuList fontSize='sm'>
				<MenuItem
					onClick={() =>
						syncInbox.mutate(undefined, {
							onSuccess: (data) =>
								toast({
									status: 'success',
									title: 'Gmail inbox synced',
									description: `Bounces: ${data?.bounced || 0}, replies: ${data?.replied || 0}`,
								}),
							onError: (err) =>
								toast({ status: 'error', title: err.message, isClosable: true }),
						})
					}>
					Refresh bounces & replies
				</MenuItem>
				<MenuItem onClick={onConnect}>Switch Gmail account</MenuItem>
				<MenuItem
					color='red.600'
					onClick={() =>
						disconnect.mutate(undefined, {
							onSuccess: () => toast({ status: 'info', title: 'Gmail disconnected' }),
							onError: (err) =>
								toast({ status: 'error', title: err.message, isClosable: true }),
						})
					}>
					Disconnect
				</MenuItem>
			</MenuList>
		</Menu>
	);
};

/**
 * Sends the lead's next email (first email or follow-up) from the user's Gmail.
 * `inlineConfirm` asks for a second click in place (for popovers); otherwise a dialog
 * shows from / to / subject. `beforeSend` runs first, e.g. to save unsaved draft edits.
 */
export const SendViaGmailButton = ({
	lead,
	companyId,
	draft,
	beforeSend,
	size = 'sm',
	inlineConfirm = false,
	isDisabled = false,
}) => {
	const toast = useToast();
	const dialog = useDisclosure();
	const [armed, setArmed] = useState(false);
	const { data: status } = useGmailStatus();
	const connect = useConnectGmail();
	const send = useSendLeadEmail(companyId);
	const { formatEmail } = useOutreachEmailSettings(companyId);

	const kind = NEXT_SEND[lead?.status];
	if (!kind) return null;
	if (status?.configured === false) return null;

	if (!status?.connected) {
		return (
			<Button
				size={size}
				variant='outline'
				leftIcon={<FaGoogle />}
				isLoading={connect.isPending}
				onClick={() =>
					connect.mutate(undefined, {
						onError: (err) =>
							toast({ status: 'error', title: err.message, isClosable: true }),
					})
				}>
				{status?.needsReconnect
					? 'Reconnect Gmail to send'
					: 'Connect Gmail to send'}
			</Button>
		);
	}

	const preview = previewFor(lead, kind, draft, formatEmail);
	const blockedReason = preview.threadMissing
		? 'The first email wasn\'t sent from Gmail, so this follow-up can\'t reply in its thread. Use "Open in mail app".'
		: !preview.to
			? 'Add a recipient email first'
			: !preview.body
				? 'This email is empty'
				: kind === 'emailed' && !preview.subject
					? 'Add a subject first'
					: '';

	const doSend = async () => {
		try {
			if (beforeSend) await beforeSend();
			await send.mutateAsync({ id: lead._id, kind });
			toast({
				status: 'success',
				title: `${EVENT_LABELS[kind]} from Gmail`,
				description: `To ${preview.to}. It's in your Gmail Sent folder.`,
				duration: 5000,
			});
			dialog.onClose();
			setArmed(false);
		} catch (err) {
			toast({ status: 'error', title: err.message, isClosable: true });
		}
	};

	const label = SEND_LABELS[kind];
	const disabled = isDisabled || Boolean(blockedReason);

	if (inlineConfirm && armed) {
		return (
			<HStack spacing={1}>
				<Button size={size} variant='ghost' onClick={() => setArmed(false)}>
					Cancel
				</Button>
				<Button
					size={size}
					colorScheme='teal'
					leftIcon={<FaPaperPlane />}
					isLoading={send.isPending}
					onClick={doSend}>
					Confirm send
				</Button>
			</HStack>
		);
	}

	return (
		<>
			<Tooltip label={blockedReason} isDisabled={!blockedReason} hasArrow>
				<Box display='inline-block'>
					<Button
						size={size}
						colorScheme='teal'
						leftIcon={<FaPaperPlane />}
						isDisabled={disabled}
						onClick={() => (inlineConfirm ? setArmed(true) : dialog.onOpen())}>
						{label}
					</Button>
				</Box>
			</Tooltip>
			{!inlineConfirm && (
				<Modal isOpen={dialog.isOpen} onClose={dialog.onClose} size='xl' scrollBehavior='inside'>
					<ModalOverlay />
					<ModalContent maxW={{ base: '96vw', md: '920px' }} w='full'>
						<ModalHeader>{label}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							<Stack spacing={2} fontSize='sm'>
								<Text>
									<b>From:</b> {status.email}
								</Text>
								<Text>
									<b>To:</b> {preview.to}
								</Text>
								<Text>
									<b>Subject:</b> {preview.subject}
								</Text>
								<Box mt={2}>
									<OutreachEmailHtmlPreview
										html={preview.bodyHtml || preview.body}
										minHeight={220}
										maxHeight={440}
									/>
								</Box>
								<Text fontSize='xs' color='gray.500'>
									Sends from your Gmail now and logs &ldquo;{EVENT_LABELS[kind]}&rdquo;
									on the timeline.
								</Text>
							</Stack>
						</ModalBody>
						<ModalFooter gap={2}>
							<Button variant='ghost' onClick={dialog.onClose}>
								Cancel
							</Button>
							<Button
								colorScheme='teal'
								leftIcon={<FaPaperPlane />}
								isLoading={send.isPending}
								onClick={doSend}>
								Send
							</Button>
						</ModalFooter>
					</ModalContent>
				</Modal>
			)}
		</>
	);
};
